import { EnrollmentStatus } from '@zorro/clients';
import { getNow, parseDateISO } from '@zorro/shared/formatters';
import { $enum } from 'ts-enum-util';

export enum EnrollmentAction {
  EDIT_ENROLLMENT_RECORD = 'EDIT_ENROLLMENT_RECORD',
  ASSIGN_ENROLLMENT = 'ASSIGN_ENROLLMENT',
  DELETE_ENROLLMENT_RECORD = 'DELETE_ENROLLMENT_RECORD',
  CHANGE_ELECTION_WINDOW = 'CHANGE_ELECTION_WINDOW',
  CHANGE_WAIVER_DETAILS = 'CHANGE_WAIVER_DETAILS',
  ADD_VISION_PLAN = 'ADD_VISION_PLAN',
  ADD_DENTAL_PLAN = 'ADD_DENTAL_PLAN',
  RESET_ELECTION = 'RESET_ELECTION',
  EDIT_ALLOWANCE = 'EDIT_ALLOWANCE',
  WAIVE_COVERAGE = 'WAIVE_COVERAGE',
  MANUAL_SUBMISSION = 'MANUAL_SUBMISSION',
  CARRIER_APPLICATION = 'CARRIER_APPLICATION',
  ENROLLMENT_CONFIRMATION = 'ENROLLMENT_CONFIRMATION',
  CHANGE_CUSTOM_CLASS_CATEGORY = 'CHANGE_CUSTOM_CLASS_CATEGORY',
  LOG_ENROLLMENT_CALL = 'LOG_ENROLLMENT_CALL',
}

export type ActionVisibility = {
  withEditEnrollmentRecord: boolean;
  withAssignEnrollment: boolean;
  withDeleteEnrollmentRecord: boolean;
  withDownloadAllPlans: boolean;
  withChangeElectionWindow: boolean;
  withSendElectionWindowLaunchEmail: boolean;
  withSendWaiverConfirmationEmail: boolean;
  withSendElectionSummaryEmail: boolean;
  withChangeWaiverDetails: boolean;
  withResetElection: boolean;
  withEditAllowance: boolean;
  withLogEnrollmentCall: boolean;
};

type ActionVisibilityProps = {
  enrollmentStatus: EnrollmentStatus;
  isEmployerAdmin?: boolean;
  isZorroOperations?: boolean;
  onboardingUntil?: string;
};

export const getActionVisibility = ({
  enrollmentStatus,
  isEmployerAdmin,
  isZorroOperations,
  onboardingUntil,
}: ActionVisibilityProps): ActionVisibility => {
  const baseVisibility = $enum
    .mapValue(enrollmentStatus)
    .with<ActionVisibility>({
      [EnrollmentStatus.PENDING_ELECTION_WINDOW]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: true,
        withSendElectionWindowLaunchEmail: true,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: false,
        withResetElection: false,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.ELECTION_ACTIVE]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: true,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: false,
        withResetElection: false,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: true,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: false,
        withResetElection: false,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.ELECTION_ACTIVE_STARTED]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: true,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: false,
        withResetElection: false,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.DEADLINE_PASSED]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: true,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: false,
        withResetElection: false,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.ELECTION_SUBMITTED]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: true,
        withChangeWaiverDetails: false,
        withResetElection: true,
        withEditAllowance: true,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.ENROLLMENT_IN_PROCESS]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: true,
        withChangeWaiverDetails: false,
        withResetElection: true,
        withEditAllowance: true,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.ENROLLMENT_CONFIRMED]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: true,
        withChangeWaiverDetails: false,
        withResetElection: true,
        withEditAllowance: true,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.ACTIVE_COVERAGE]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: false,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: true,
        withChangeWaiverDetails: false,
        withResetElection: true,
        withEditAllowance: true,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.COVERAGE_ENDED]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: false,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: true,
        withChangeWaiverDetails: false,
        withResetElection: true,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.WAIVED_ELECTION]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: true,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: true,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: true,
        withResetElection: true,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.WAIVED_COVERAGE]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: false,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: true,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: true,
        withResetElection: true,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
      [EnrollmentStatus.NO_ENROLLMENTS]: {
        withEditEnrollmentRecord: true,
        withAssignEnrollment: true,
        withDeleteEnrollmentRecord: true,
        withDownloadAllPlans: false,
        withChangeElectionWindow: false,
        withSendElectionWindowLaunchEmail: false,
        withSendWaiverConfirmationEmail: false,
        withSendElectionSummaryEmail: false,
        withChangeWaiverDetails: false,
        withResetElection: false,
        withEditAllowance: false,
        withLogEnrollmentCall: true,
      },
    });

  if (isEmployerAdmin) {
    return {
      ...baseVisibility,
      withEditEnrollmentRecord: false,
      withAssignEnrollment: false,
      withDeleteEnrollmentRecord: false,
      withChangeElectionWindow: false,
      withLogEnrollmentCall: false,
      withEditAllowance: false,
      withResetElection: getEmployerAdminResetElection(
        enrollmentStatus,
        onboardingUntil
      ),
    };
  }

  if (!isZorroOperations) {
    return {
      ...baseVisibility,
      withEditAllowance: false,
    };
  }

  return baseVisibility;
};

function getEmployerAdminResetElection(
  enrollmentStatus: EnrollmentStatus,
  onboardingUntil?: string
): boolean {
  if (enrollmentStatus === EnrollmentStatus.ELECTION_SUBMITTED) {
    return true;
  }
  if (
    enrollmentStatus === EnrollmentStatus.WAIVED_ELECTION &&
    onboardingUntil
  ) {
    return !parseDateISO(onboardingUntil).isBefore(getNow(), 'day');
  }
  return false;
}
